/**
 * ParkingRecallOverlay, the "Find my car" view.
 *
 * Presents the remembered parking spot plus optional photo + note, asks the
 * server for short wayfinding prose from the visitor's current QR, and
 * includes a "Still parked here?" confirmation when the anchor is stale.
 *
 * Purely anonymous. No identity. No background tracking. The overlay opens
 * when the visitor taps the pinned recall row or the "Find my car" chip.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, X } from 'lucide-react';
import { cn } from '@tendedloop/ui';
import { clearAnchor, isStale, relativeTime, updateAnchor, type ParkedSpot } from '@/lib/parking';

interface ParkingRecallOverlayProps {
  open: boolean;
  onClose: () => void;
  spot: ParkedSpot;
  /** QR code of the visitor's current location, source for directions. */
  fromQrCode: string;
  /** Amenity id of the visitor's current location, cache key for directions. */
  fromAmenityId: string;
  apiUrl: string;
  onCleared: () => void;
  /**
   * Optional outdoor-Maps destination string (tenant + building names, or
   * a verbatim address from the KB). When provided, an "Open in Google Maps"
   * deep-link button renders below the indoor wayfinding text — using the
   * universal Maps URL scheme, no API key, no auth.
   */
  outdoorMapsDestination?: string;
}

export function ParkingRecallOverlay(props: ParkingRecallOverlayProps) {
  const { t, i18n } = useTranslation('visitor');
  const { open, onClose, spot, fromQrCode, fromAmenityId, apiUrl, onCleared, outdoorMapsDestination } = props;

  // Cache key includes language so switching locales refetches instead of
  // showing stale English directions on a Hebrew page.
  const cacheKey = `${fromAmenityId}:${i18n.language}`;
  const [directions, setDirections] = useState<string | null>(spot.directions?.[cacheKey] || null);
  const [directionsBusy, setDirectionsBusy] = useState(false);
  const [directionsError, setDirectionsError] = useState(false);
  const [stale, setStale] = useState(isStale(spot));
  const [staleConfirmed, setStaleConfirmed] = useState(false);

  // Close on Escape, matches IssueSheet / BuildingSheet pattern. The dialog
  // advertises modal semantics above; AT users expect Escape to dismiss it.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setStale(isStale(spot));

    // If we're at the parking spot itself, don't bother with routing.
    if (fromAmenityId === spot.amenityId) {
      setDirections(null);
      return;
    }

    // Hit cache first, directions are idempotent per (anchor, from, lang).
    const cached = spot.directions?.[cacheKey];
    if (cached) {
      setDirections(cached);
      return;
    }

    setDirectionsBusy(true);
    setDirectionsError(false);
    fetch(`${apiUrl}/api/rag/parking/recall-directions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        qrCode: fromQrCode,
        anchorAmenityId: spot.amenityId,
        language: i18n.language,
      }),
    })
      .then((r) => r.json())
      .then((body) => {
        if (!body?.success || !body.data?.text) throw new Error('no text');
        const text = body.data.text as string;
        setDirections(text);
        updateAnchor(spot.tenantId, spot.buildingId, {
          directions: { ...(spot.directions || {}), [cacheKey]: text },
        });
      })
      .catch(() => {
        setDirectionsError(true);
      })
      .finally(() => setDirectionsBusy(false));
  }, [open, apiUrl, fromAmenityId, fromQrCode, spot, cacheKey, i18n.language]);

  if (!open) return null;

  const handleStillThere = () => {
    const next = updateAnchor(spot.tenantId, spot.buildingId, { lastConfirmedTs: Date.now() });
    if (!next) return;
    // Flash a short "Confirmed" state so the banner doesn't just silently
    // disappear. ~900ms feels long enough to register without blocking flow.
    setStaleConfirmed(true);
    setTimeout(() => {
      setStale(false);
      setStaleConfirmed(false);
    }, 900);
  };

  const handleClear = () => {
    clearAnchor(spot.tenantId, spot.buildingId);
    onCleared();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="parking-recall-title"
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 animate-fade-in bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className={cn(
          'relative w-full max-w-lg animate-slide-up rounded-t-3xl bg-white p-5 sm:rounded-3xl',
          'max-h-[88vh] overflow-y-auto overscroll-contain safe-area-bottom',
        )}
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-4 h-1 w-10 rounded-full sm:hidden"
          style={{ background: 'color-mix(in srgb, var(--dir-ink-muted) 28%, transparent)' }}
        />

        {/* Header — display font/style flips by direction. */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div
              className="text-[15px] font-medium"
              style={{ color: 'var(--dir-ink-muted)', fontFamily: 'var(--dir-font-body)' }}
            >
              {t('v2.recall.heading', 'Your parked car')}
            </div>
            <h3
              id="parking-recall-title"
              className="mt-1 text-[22px] font-bold leading-tight tracking-[-0.02em]"
              style={{
                color: 'var(--dir-ink-primary)',
                fontFamily: 'var(--dir-font-display)',
                fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
              }}
            >
              {spot.amenityName}
            </h3>
            <p className="mt-1 text-[15px] font-medium" style={{ color: 'var(--dir-ink-muted)' }}>
              {spot.locationName}
              {' · '}
              {relativeTime(spot.ts, i18n.language)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('v2.close', 'Close')}
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-all active:scale-95"
            style={{
              background: 'color-mix(in srgb, var(--dir-ink-muted) 12%, var(--dir-bg-paper))',
              color: 'var(--dir-ink-secondary)',
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Photo — postcard-framed in either direction; the card border var
            naturally renders 1px charcoal in Atelier, soft shadow in Concierge. */}
        {spot.photoThumb && (
          <div className="mt-2 overflow-hidden rounded-2xl" style={{ border: 'var(--dir-card-border)' }}>
            <img
              src={spot.photoThumb}
              alt={t('v2.recall.photoAlt', 'Photo of your parking spot')}
              className="h-56 w-full object-cover"
            />
          </div>
        )}

        {/* Note — italic in Atelier (display-italic var pulls the serif), upright in Concierge */}
        {spot.note && (
          <div
            className="mt-3 rounded-2xl px-4 py-3 text-[15px] font-medium"
            style={{
              background: 'color-mix(in srgb, var(--dir-ink-muted) 10%, var(--dir-bg-paper))',
              color: 'var(--dir-ink-primary)',
              fontFamily: 'var(--dir-font-display)',
              fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
            }}
          >
            “{spot.note}”
          </div>
        )}

        {/* Stale confirmation (only when half-TTL has elapsed). When the
            visitor taps "Still there", the amber banner morphs briefly into
            a green "Confirmed" state before collapsing, so the tap has
            visible feedback instead of just vanishing. */}
        {stale && (
          <div
            className={cn(
              'mt-4 rounded-2xl border px-4 py-3 transition-colors duration-200',
              staleConfirmed ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50',
            )}
            aria-live="polite"
          >
            {staleConfirmed ? (
              <div className="flex items-center gap-2 text-[15px] font-semibold text-emerald-900">
                <span
                  aria-hidden="true"
                  className="flex h-6 w-6 items-center justify-center rounded-full"
                  style={{ background: 'var(--dir-accent-primary)', color: 'var(--dir-accent-on)' }}
                >
                  <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" strokeWidth={3.5} stroke="currentColor">
                    <path d="M4 10l4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {t('v2.recall.confirmed', 'Confirmed')}
              </div>
            ) : (
              <>
                <div className="text-[15px] font-medium text-amber-900">
                  {t('v2.recall.stillHere', 'Still parked here?')}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={handleStillThere}
                    className="flex min-h-[48px] flex-1 items-center justify-center rounded-full bg-amber-900 px-4 py-3 text-[15px] font-semibold text-white transition-transform active:scale-[0.97]"
                  >
                    {t('v2.recall.stillThere', 'Still there')}
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="flex min-h-[48px] flex-1 items-center justify-center rounded-full border border-amber-300 bg-white px-4 py-3 text-[15px] font-semibold text-amber-900 transition-transform active:scale-[0.97]"
                  >
                    {t('v2.recall.clear', 'Clear')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Directions — AI-prose route. Card border + ink read from tokens. */}
        <div className="mt-4">
          <div
            className="flex items-center gap-2 text-[15px] font-semibold"
            style={{ color: 'var(--dir-ink-primary)', fontFamily: 'var(--dir-font-body)' }}
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {t('v2.recall.directions', 'Directions back')}
          </div>
          <div
            className="mt-2 rounded-2xl px-4 py-3 text-[15px] leading-relaxed"
            style={{
              border: 'var(--dir-card-border)',
              background: 'var(--dir-bg-paper)',
              color: 'var(--dir-ink-primary)',
            }}
          >
            {directionsBusy && (
              <div className="flex items-center gap-2" style={{ color: 'var(--dir-ink-muted)' }}>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('v2.recall.generating', 'Working it out…')}
              </div>
            )}
            {!directionsBusy && directions && <p>{directions}</p>}
            {!directionsBusy && !directions && fromAmenityId === spot.amenityId && (
              <p style={{ color: 'var(--dir-ink-muted)' }}>
                {t('v2.recall.youAreHere', "You're already at your parking spot.")}
              </p>
            )}
            {!directionsBusy && !directions && directionsError && (
              <p style={{ color: 'var(--dir-ink-muted)' }}>
                {t('v2.recall.directionsFallback', 'Head back toward the main entrance and follow signs to {{name}}.', {
                  name: spot.amenityName,
                })}
              </p>
            )}
          </div>
          {/* Outdoor Google Maps deep-link — opens Maps with destination =
              tenant + building name (or KB-listed address). Universal URL
              scheme, no API key, no auth. Renders ONLY when a destination
              has been passed in (so non-Bar-Ilan tenants without a real
              address don't get an empty button). */}
          {outdoorMapsDestination && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                outdoorMapsDestination,
              )}&travelmode=walking`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-1.5 rounded-full px-3.5 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.97]"
              style={{ background: '#1a73e8' }}
              data-testid="parking-recall-maps-deeplink"
            >
              📍 {t('v3.recall.walkMeBack', 'Walk me back')}
            </a>
          )}
        </div>

        {/* Footer, clear action when not stale */}
        {!stale && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex min-h-[48px] items-center px-3 py-3 text-[15px] font-medium underline underline-offset-2 transition-colors"
              style={{ color: 'var(--dir-ink-muted)' }}
            >
              {t('v2.recall.clearLink', 'Clear this spot')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
